import { isCalendarDate } from './dates.js';
import { ZERO, type Money } from './money.js';
import { routeToPeriod, type PeriodStart, type PeriodSummary } from './period.js';
import type { ReceiptKind } from './commission.js';
import type { Direction, LedgerAccountType } from './ledger.js';

/**
 * A transaction — one thing that happened, in the words a property manager would use.
 *
 * "Rent received from the Rewitis, $600." That is one event. In double entry it is three
 * journals: the money arriving, the money being allocated to the owners, and the fee coming
 * back out. All three are correct and all three have to exist, and none of them is the thing
 * the operator did.
 *
 * So there are three levels, and each has a reader:
 *
 *   transaction  what happened          a property manager, an owner reading a statement
 *   journal      the accounting steps   an accountant
 *   entry        the debits and credits an auditor
 *
 * A transaction carries an AMOUNT and no accounts. The amount is what the event was worth —
 * $600 of rent received — stated by whoever posts it rather than worked out from the entries
 * afterwards, because the value of an event is a fact about what somebody did and does not
 * always appear as a single figure in what the books did. Whose money it was, on the other
 * hand, is only ever a property of the entries: one account on the header would make a
 * property owned by two people unrepresentable.
 *
 * PURE. This module names the kinds of event; `packages/db/src/transactions.ts` opens them.
 */

/**
 * What kind of event a transaction records.
 *
 * NOT the same list as `JournalSource`, and the difference is the point. A source says how one
 * JOURNAL came to exist — `allocation`, `commission` — and those are steps, not events. A kind
 * says what HAPPENED. One `rent_receipt` contains two `allocation` journals and one
 * `commission` journal, and no source describes the receipt itself.
 *
 * Forward-looking, like `JournalSource`: `disbursement`, `bond` and `reversal` have no writer
 * until M3/M4, and are listed so the enum is not migrated once per milestone. Adding a value is
 * a deliberate act — `ledger.drift.test.ts` pins this list against the column.
 */
export const TRANSACTION_KINDS = [
  'manual',
  'opening_balance',
  'rent_receipt',
  'owner_receipt',
  'suspense_receipt',
  'disbursement',
  /*
   * The agency drawing its own income out of the trust account — TRUST_ACCOUNTING.md §1.
   *
   * ITS OWN KIND RATHER THAN `manual`, and it sits beside `disbursement` on purpose: both are
   * money LEAVING the trust bank account, and the two questions an auditor asks of a trust
   * account are what went out to beneficiaries and what the agency drew for itself. A commission
   * transfer posted as a manual entry would answer the second one only by reading narratives.
   */
  'commission_transfer',
  'bond',
  /*
   * A supplier's invoice, in its two events — M4, split in `0064`.
   *
   *   bill          DR owner(s)   CR supplier    the owners' money is COMMITTED to the invoice
   *   bill_payment  DR supplier   CR trust_bank  the money LEAVES the trust account
   *
   * ONE KIND COVERED BOTH UNTIL `0064`, on `bond`'s reasoning: they are one story to an operator,
   * and "what did we do about BL000012" should not be a question about two unrelated rows. What
   * broke that is that only the second one leads anywhere. A bill payment is a line of a direct
   * credit batch and a line of a supplier's remittance advice; an approval is neither, because no
   * money has left the building. A transaction list that cannot tell them apart cannot offer
   * either document on the right rows — it offers both on a superset and is refused afterwards by
   * a server that could tell. The bill's own page still shows the pair together, which is where
   * the one-story argument was actually being served.
   *
   * NEITHER IS IN `RECEIPT_TRANSACTION_KINDS` below. Both move money AWAY from a beneficiary, so
   * labelling either as an arrival would report a payment out as fresh money in, the first time a
   * bill payment reaches outgoing-line matching.
   */
  'bill',
  'bill_payment',
  /*
   * Rent handed back to the tenant who paid it — M4.
   *
   * ITS OWN KIND RATHER THAN `reversal`, and the distinction is one an auditor asks about. A
   * reversal says the receipt should never have been posted; a refund says it was right and the
   * money is being returned anyway — an overpayment, a tenancy ended mid-cycle, a rent reduction
   * ordered after the fact. The journals are the same shape (`rentRefundJournals` mirrors
   * `rentReceiptJournals`), which is exactly why the LABEL has to differ: two events that post
   * identical entries and mean different things are two events a list must be able to tell apart.
   *
   * DELIBERATELY NOT IN `RECEIPT_TRANSACTION_KINDS` below, for the reason `bill` is not: it moves
   * money away from a beneficiary, and calling it an arrival would report a payment out as fresh
   * money in the first time it reaches outgoing-line matching.
   */
  'rent_refund',
  'rent_released',
  'rent_credit_return',
  /*
   * A tenant paying a tenancy invoice — the water bill recovered, the lease break fee.
   *
   * ITS OWN KIND, AND IT IS IN `RECEIPT_TRANSACTION_KINDS` BELOW, which is the pair of decisions
   * worth reading together. It is a receipt because money genuinely arrives in the trust bank and
   * a bank line will exist for it — labelling it anything else would leave that deposit reported
   * as unposted by the matcher for ever. It is its own KIND because the fee treatment and the
   * beneficiary are both different from rent's: no management fee is charged, and the credit goes
   * to the owner gross or to the agency with GST, depending on what the invoice said.
   *
   * NOT `rent_receipt`: a list that cannot tell rent from a lease break fee cannot answer "what
   * did this tenancy actually pay in rent this year", which is the figure a rent review and a
   * Tribunal application are both built on.
   *
   * ISSUING THE INVOICE HAS NO KIND HERE AT ALL, and that is the feature's central decision
   * rather than an omission — see the argument at the top of `tenancyInvoice.ts`. Raising the
   * charge posts no journal, so there is no transaction to name.
   */
  'invoice_receipt',
  /*
   * An inspection fee, charged when a report is signed off — M4.
   *
   * ITS OWN KIND, and the alternative it is arguing with is `manual`. The event is a charge
   * against an owner's balance with no bank line either side of it, which is what a manual journal
   * looks like too — so without a kind of its own, "what did we charge this owner for inspections
   * last year" is a question answerable only by reading narratives, and the transaction list shows
   * a row that could be anything.
   *
   * NOT `commission_transfer`, which is the agency DRAWING money it has already earned. This is
   * the earning. The journal beneath it carries `source: 'commission'` for exactly that reason —
   * it is the same step a rent receipt's third journal is, happening on its own.
   *
   * NOT IN `RECEIPT_TRANSACTION_KINDS`: it moves money away from a beneficiary, so labelling it an
   * arrival would report a charge as fresh money in the first time it reaches line matching.
   */
  'inspection_fee',
  'reversal',
] as const;

export type TransactionKind = (typeof TRANSACTION_KINDS)[number];

/** What each kind is called in front of a person. Sentence case — these appear mid-sentence. */
export const TRANSACTION_KIND_LABELS: Readonly<Record<TransactionKind, string>> = {
  manual: 'Manual entry',
  opening_balance: 'Opening balance',
  /*
   * ONE KIND PER BENEFICIARY, and the split is not cosmetic. These three are what money arriving
   * can be for, and each has a different consequence somebody has to be able to see at a glance:
   *
   *   rent_receipt      charged a management fee and GST, and settled a rent charge
   *   invoice_receipt   settled a tenancy invoice — no fee, and the credit may be the AGENCY's
   *   owner_receipt     charged NO fee — the fee is on rent collected, not on the owner's own money
   *   suspense_receipt  belongs to nobody yet: it ages, and it blocks a month close (§5)
   *
   * They were one `receipt` kind until an operator pointed out that a list which cannot tell
   * owner funds from unidentified money is a list where suspense accumulates unnoticed — which
   * is the one thing §5 exists to prevent.
   */
  rent_receipt: 'Rent receipt',
  owner_receipt: 'Owner receipt',
  suspense_receipt: 'Suspense receipt',
  disbursement: 'Disbursement',
  /*
   * "Commission transfer", not "Commission" — the event is the MOVEMENT, not the earning. The
   * fee is earned inside a rent receipt and shows there; this row is the agency taking it out.
   */
  commission_transfer: 'Commission transfer',
  bond: 'Bond',
  /*
   * TWO WORDS FOR TWO EVENTS, and the pair has to be read together to see why each is right.
   *
   * "Bill" is the APPROVAL — the posting that reduces an owner's balance and commits it to the
   * invoice. It keeps the bare noun because that is the moment the agency took the bill on.
   *
   * "Bill payment" is the money leaving. Not "Paid" and not "Supplier payment": the operator
   * scanning this column is looking for what happened to a BILL, and the word that answers them
   * has to start the same way as the row above it so the two sort and read as a pair.
   */
  bill: 'Bill',
  bill_payment: 'Bill payment',
  // "Rent refund", never "Refund" on its own. A bond refund is the other refund this product
  // posts, and a list that called both of them the same word would need its narrative read to
  // tell whose money went back.
  rent_refund: 'Rent refund',
  rent_released: 'Rent released',
  rent_credit_return: 'Rent credit restored',
  // "Invoice payment", not "Invoice": the event is the tenant PAYING. The invoice itself is a
  // document on its own page with an IV number, and plenty of them are never paid at all — which
  // is precisely the state the Tenancy invoices list exists to show.
  invoice_receipt: 'Invoice payment',
  // "Inspection fee", not "Inspection": the event is the CHARGE. The inspection itself is a
  // record on its own page, and most of them are never charged for at all.
  inspection_fee: 'Inspection fee',
  reversal: 'Reversal',
};

export function transactionKindLabel(kind: TransactionKind): string {
  return TRANSACTION_KIND_LABELS[kind];
}

/**
 * The kinds that mean "money arrived", in order of how much is known about whose it is.
 *
 * ORDERED, because `receiptTransactionKind` reads the order as precedence and the reconciliation
 * code asks "was this event a receipt at all". Both would otherwise be a list of string literals
 * repeated in two files, and the one that got missed when a kind was added would be a bank line
 * silently reported as unposted.
 */
export const RECEIPT_TRANSACTION_KINDS = [
  'rent_receipt',
  /*
   * Second, behind rent and ahead of owner funds, and the order is precedence on a MIXED receipt.
   *
   * Behind rent for the reason rent leads at all: rent is the kind that charged a management fee,
   * and a list where a fee-bearing receipt looks like a fee-free one is a list where nobody
   * notices a fee that was never taken.
   *
   * Ahead of owner funds because an invoice payment SETTLES something. A mixed receipt labelled
   * "Owner receipt" would sit in the list looking like money an owner wired in, while the part of
   * it that discharged a tenant's debt — and, for an agency-destined charge, earned the agency
   * income — went unremarked. Owner funds are the passive case: money arrived and is being held.
   */
  'invoice_receipt',
  'owner_receipt',
  'suspense_receipt',
] as const;

export type ReceiptTransactionKind = (typeof RECEIPT_TRANSACTION_KINDS)[number];

export function isReceiptTransactionKind(kind: string): kind is ReceiptTransactionKind {
  return (RECEIPT_TRANSACTION_KINDS as readonly string[]).includes(kind);
}

/** What each kind of receipt LINE makes the transaction above it. */
const RECEIPT_KIND_TO_TRANSACTION: Readonly<Record<ReceiptKind, ReceiptTransactionKind>> = {
  rent: 'rent_receipt',
  invoice: 'invoice_receipt',
  owner: 'owner_receipt',
  suspense: 'suspense_receipt',
};

/**
 * What one receipt's lines make the event as a whole.
 *
 * ONE LINE IS THE NORMAL CASE — the trust page splits a multi-line entry into one transaction per
 * line before any of it is posted — and then this is simply the line's own kind.
 *
 * A MIXED RECEIPT TAKES THE MOST CONSEQUENTIAL KIND, in the order of `RECEIPT_TRANSACTION_KINDS`.
 * Any rent at all makes it a rent receipt because that is the kind that charged a fee, and a list
 * where the two look alike is a list where nobody notices a fee that was never taken. Owner funds
 * beat suspense on the same reasoning: the named beneficiary is the part somebody has to act on.
 *
 * The mixed case cannot hide money either way — the suspense BALANCE is what §5 ages and what
 * refuses a month close, and that is computed from the ledger accounts rather than from this
 * label.
 *
 * No lines at all is not a receipt, so there is nothing honest to return; the caller refuses the
 * empty input before it gets here (`postReceipt`), and this returns `suspense_receipt` rather
 * than throwing, because unidentified is what money with nobody attached to it is.
 */
export function receiptTransactionKind(lineKinds: readonly ReceiptKind[]): ReceiptTransactionKind {
  for (const kind of RECEIPT_TRANSACTION_KINDS) {
    if (lineKinds.some((line) => RECEIPT_KIND_TO_TRANSACTION[line] === kind)) return kind;
  }
  return 'suspense_receipt';
}

/**
 * The kinds a person may post by hand.
 *
 * Deliberately short, and the omissions are the point. `rent_receipt` is not here: a receipt
 * charges a management fee at a rate read off the property and a GST status read off the
 * agency, and a hand-built one labelled `rent_receipt` would sit in the list looking exactly
 * like a real one while having charged nobody anything. Same for `disbursement`, `bond` and
 * `reversal` — each has, or will have, a path that does the rest of the work.
 *
 * What is left is the two that genuinely have no other route: an opening balance when an agency
 * migrates its books in, and a manual entry for everything else.
 */
export const MANUAL_TRANSACTION_KINDS = ['manual', 'opening_balance'] as const;

export type ManualTransactionKind = (typeof MANUAL_TRANSACTION_KINDS)[number];

export function isManualKind(kind: string): kind is ManualTransactionKind {
  return (MANUAL_TRANSACTION_KINDS as readonly string[]).includes(kind);
}

// ── Validation ───────────────────────────────────────────────────────────────

/** The header, without the journals — what has to be true before a TX number is spent. */
export interface TransactionDraft {
  kind: TransactionKind;
  /** The day the event happened, YYYY-MM-DD. */
  occurredOn: string;
  /** What the event was worth, in cents. Never negative; the entries carry direction. */
  amountCents: Money;
  narrative: string;
  /** How many journals it will post. Zero is the case worth naming. */
  journalCount: number;
}

/**
 * What is wrong with a transaction header. Empty means it can be opened.
 *
 * Same idiom as `journalIssues`: a list a human can read rather than a thrown error, and
 * checked here so a refusal arrives as a sentence rather than as a CHECK violation — and so a
 * TX number is not spent on an event that was never going to post.
 */
export function transactionIssues(draft: TransactionDraft): string[] {
  const issues: string[] = [];

  if (draft.narrative.trim().length === 0) {
    // This is the line that appears on an owner's statement. "" is not an event.
    issues.push('Say what happened — the narrative is what appears on a statement');
  }

  if (!isCalendarDate(draft.occurredOn)) {
    issues.push(`Not a valid date: ${JSON.stringify(draft.occurredOn)}`);
  }

  if (draft.amountCents < ZERO) {
    // Direction lives on the entries, never in the sign of a total. Permitting a negative here
    // would give every movement two spellings, and two spellings of one fact drift apart.
    issues.push('A transaction cannot be worth a negative amount — the entries carry direction');
  }

  if (draft.journalCount < 1) {
    // A transaction is a claim that something happened. With no journals beneath it, it is a
    // claim that money moved while the books show none of it — a narrative with no accounting.
    issues.push('A transaction needs at least one journal — nothing happened otherwise');
  }

  return issues;
}

// ── Reversal ─────────────────────────────────────────────────────────────────

/**
 * What a reversal is dated, given where it would land.
 *
 * THE DEFAULT IS THE ORIGINAL'S OWN DATE. A correction posted in the same open month is an
 * adjustment to that month, and dating it anywhere else would move the event away from the thing
 * it is undoing for no reason a reader could reconstruct.
 *
 * WHEN THE BOOKS HAVE MOVED ON, THE DATE MOVES WITH THEM. A transaction inside a signed-off
 * month cannot be reversed into that month — invariant 6, and 0011's deferred trigger refuses it
 * regardless of what any of this decides. So the reversal is dated to the FIRST DAY of the period
 * it will actually post into. The alternative is a row dated into August sitting in September's
 * books, which is legitimate for a late item that genuinely happened in August and is a lie about
 * a correction that happened today: the event this row records is somebody undoing something now.
 *
 * THE TEST IS `routeToPeriod`, not "is the original's month locked", and the difference is a real
 * case. A transaction dated into an open May, when June has since been signed off, also cannot be
 * reversed into May — June was closed on an opening balance that a new May entry would move
 * underneath it. Asking the router means this rule and the routing rule cannot disagree; asking
 * about the original's own period would have missed exactly that arrangement.
 *
 * `movedForward` is for the screen, which has to say the date changed and why. A reversal that
 * silently lands two months from the thing it reverses is a figure somebody reconciles twice.
 */
export interface ReversalDating {
  /** The date the reversal carries, `YYYY-MM-DD`. */
  occurredOn: string;
  /** True when the original's date could not be used and the period start was taken instead. */
  movedForward: boolean;
  /** The period it will post into either way. */
  periodStartsOn: PeriodStart;
}

export function reversalOccurredOn(
  originalOccurredOn: string,
  periods: readonly PeriodSummary[],
): ReversalDating {
  const routed = routeToPeriod(originalOccurredOn, periods);

  return routed.lateItem
    ? { occurredOn: routed.startsOn, movedForward: true, periodStartsOn: routed.startsOn }
    : { occurredOn: originalOccurredOn, movedForward: false, periodStartsOn: routed.startsOn };
}

/**
 * What a reversal is called, on the list and on a statement.
 *
 * NAMES THE REF, not just the narrative. "Reversal of: Rent received — 12 Kowhai St" is
 * ambiguous the moment an agency has receipted that tenancy twice, and the pair of rows an
 * auditor is trying to match up are the two this string is the only prose link between.
 */
export function reversalNarrative(originalRef: string, originalNarrative: string): string {
  return `Reversal of ${originalRef} — ${originalNarrative}`;
}

/** What a transaction has to be before it can be reversed. Empty means it can. */
export interface ReversalCandidate {
  /** `TX000128`, for the sentences below — a uuid in a refusal is a support ticket. */
  ref: string;
  /** The ref of the transaction that already reverses this one, when one does. */
  reversedByRef: string | null;
}

/**
 * Why this transaction cannot be reversed. Empty means go ahead.
 *
 * ONE RULE, and it is the one the database also enforces (`transactions_org_reversal_unique`).
 * Reversing twice takes the money back twice, and every invariant survives it — which is exactly
 * why it needs saying here as well, in words an operator can act on.
 *
 * WHAT IS DELIBERATELY NOT REFUSED: a locked period (the reversal posts to the next open one and
 * says so), a transaction that is itself a reversal (undoing a wrong correction is an ordinary
 * act), and a reversal that would leave a beneficiary negative. That last one is refused, but not
 * here — it depends on balances this module cannot see, and `postingIssues` already produces the
 * sentence naming the account and the shortfall.
 */
export function reversalIssues(candidate: ReversalCandidate): string[] {
  const issues: string[] = [];

  if (candidate.reversedByRef !== null) {
    issues.push(
      `${candidate.ref} has already been reversed by ${candidate.reversedByRef}. ` +
        'Reversing it again would take the money back a second time.',
    );
  }

  return issues;
}

// ── What a transaction does to the trust balance ──────────────────────────────

/**
 * The two columns a person reads a trust account in.
 *
 * CONVENTION, and it is the OPPOSITE of the journals underneath. Here a credit means the trust
 * balance went UP — money arrived, as a bank statement reads it. In the ledger, receiving rent
 * DEBITS the trust bank, because a bank account is an asset. Both are correct and they are not
 * the same sentence, so anything displaying these has to say which one it is speaking. The
 * precedent is `normalisedBalance`: the raw ledger convention is flipped once, at the boundary.
 */
export interface TrustMovement {
  /** Money off the beneficiary ledgers. Paid out, or taken from one beneficiary. */
  debitCents: Money;
  /** Money onto them. Received, or given to one beneficiary. */
  creditCents: Money;
}

export const NO_MOVEMENT: TrustMovement = { debitCents: ZERO, creditCents: ZERO };

/** Credits less debits — what the transaction did to the trust balance, signed. */
export function netEffect(movement: TrustMovement): Money {
  return movement.creditCents - movement.debitCents;
}

/** An entry, reduced to what the movement rule reads. */
export interface MovementEntry {
  accountId: string;
  accountType: LedgerAccountType;
  direction: Direction;
  /** Always positive; the direction carries the sign. */
  amountCents: Money;
}

/**
 * What a set of entries does to the trust balance, in the two columns above.
 *
 * THE RULE, and both halves of it earn their place.
 *
 * NET EACH ACCOUNT FIRST. A rent receipt credits the tenancy $600 and debits it $600 again as
 * the money moves on to the owners. Summing the entries as they stand reports $600 in and $600
 * out — true about the journals, and a lie about what happened. Netting per account leaves only
 * what each beneficiary was actually left holding.
 *
 * TRUST BANK IS EXCLUDED. It is the other side of the trust equation, not a beneficiary, so
 * counting it would double every figure. What remains is precisely the liability side, which by
 * invariant 2 tracks the bank exactly — and that is why `netEffect` can be called "the effect on
 * the trust balance" without computing the bank separately. It is a consequence of the rule, not
 * a coincidence being relied on quietly.
 *
 * Worked through, the three cases that pin it:
 *
 *   Rent received $600, 8% + GST   tenancy  +600 −600 =     0   (pass-through, drops out)
 *                                  owner    +600 −55.20 = +544.80  credit
 *                                  fees                    +48.00  credit
 *                                  GST                      +7.20  credit
 *                                  → debit 0, credit 600.00, net +600.00
 *
 *   $200 from owner A to owner B   owner A            −200.00  debit
 *                                  owner B            +200.00  credit
 *                                  → debit 200, credit 200, net 0
 *
 *   Paid an owner $150             owner              −150.00  debit
 *                                  (bank excluded)
 *                                  → debit 150, credit 0, net −150.00
 *
 * THIS IS THE SAME RULE the transaction list computes in SQL (`trustMovements` in
 * @promanage/db). It lives here so a queued draft and the posted row it becomes cannot disagree
 * about what the operator is about to do — a preview that differs from the result is worse than
 * no preview.
 */
export function trustMovement(entries: readonly MovementEntry[]): TrustMovement {
  const perAccount = new Map<string, Money>();

  for (const entry of entries) {
    if (entry.accountType === 'trust_bank') continue;
    const signed = entry.direction === 'credit' ? entry.amountCents : -entry.amountCents;
    perAccount.set(entry.accountId, (perAccount.get(entry.accountId) ?? ZERO) + signed);
  }

  let debitCents = ZERO;
  let creditCents = ZERO;

  for (const net of perAccount.values()) {
    if (net > ZERO) creditCents += net;
    else if (net < ZERO) debitCents -= net;
  }

  return { debitCents, creditCents };
}

/** Add up movements — the footer under a list of them. */
export function totalMovement(movements: readonly TrustMovement[]): TrustMovement {
  let debitCents = ZERO;
  let creditCents = ZERO;

  for (const movement of movements) {
    debitCents += movement.debitCents;
    creditCents += movement.creditCents;
  }

  return { debitCents, creditCents };
}
